import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useContext } from "react";
import { useDispatch } from "react-redux";
import {
  clearClient,
  setClientID,
  setProjectID,
} from "../../../../Redux/Actions/actions";
import { fetchAdminRows } from "../../Admin/AdminQueries/useAdminRows";
import { fetchClientRows } from "../../Client/ClientQueries/useClientRows";
import { DashboardContext } from "../../DashboardContext/DashboardContextProvider";
import "./styles/dashboard-app-bar.css";

function DashboardAppBar() {
  const {
    queryClient,
    navigate,
    clientId,
    isAdmin,
    heading,
    openModal,
    openSidebar,
    setOpenSidebar,
    userId,
  } = useContext(DashboardContext);
  const dispatch = useDispatch();

  const updateFormStatus = async (data) => {
    const { clientId, projectId, requestStatus, userId } = data;
    try {
      await axios.patch(`/api/update-form-status/${clientId}/${projectId}/`, {
        requestStatus,
        userId,
      });
    } catch (error) {
      console.error("Error updating form status:", error);
    }
  };

  const updateFormMutation = useMutation({
    mutationFn: updateFormStatus,
    onSuccess: async () => {
      dispatch(setProjectID(null));

      if (isAdmin) {
        dispatch(clearClient());
        dispatch(setProjectID(null));
        dispatch(setClientID(null));
        await queryClient.ensureQueryData({
          queryKey: ["adminRows"],
          queryFn: () => fetchAdminRows(isAdmin),
        });
        navigate("/dashboard/admin", { replace: true });
      } else {
        dispatch(setProjectID(null));
        await queryClient.ensureQueryData({
          queryKey: ["clientRows", clientId],
          queryFn: () => fetchClientRows(clientId),
        });
        navigate(`/dashboard/client/${clientId}`, { replace: true });
      }
    },
    onError: (error) => {
      console.error("Error updating form status:", error);
    },
    onSettled: async () => {
      if (isAdmin) {
        await queryClient.invalidateQueries({
          queryKey: ["adminRows"],
          refetchType: "all",
        });
      } else {
        await queryClient.invalidateQueries({
          queryKey: ["clientRows", clientId],
          refetchType: "all",
        });
      }
    },
  });
  // event handler for navigating to the form page, and clearing redux state if necessary
  const handleCreateNewRequest = async (e) => {
    if (isAdmin) {
      dispatch(clearClient());
      dispatch(setClientID(null));
      dispatch(setProjectID(null));
      navigate("/dashboard/form");
    } else {
      dispatch(setProjectID(null));
      navigate(`/dashboard/form/${clientId}`);
    }
  };

  const handleReturnToDashboard = async (e) => {
    // e.preventDefault();

    if (isAdmin) {
      dispatch(clearClient());
      dispatch(setClientID(null));
      dispatch(setProjectID(null));
      await queryClient.invalidateQueries({
        queryKey: ["adminRows"],
        refetchType: "all",
      });
      navigate("/dashboard/admin", { replace: true });
    } else {
      dispatch(setProjectID(null));
      await queryClient.invalidateQueries({
        queryKey: ["clientRows", clientId, userId],
        refetchType: "all",
      });
      navigate(`/dashboard/client/${clientId}`, { replace: true });
    }
  };

  return (
    <AppBar position="fixed" id="app-bar">
      <Toolbar id="app-bar-toolbar">
        <div id="left-portal">
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            className="sidebar-hamburger"
            id="sidebar-hamburger"
            onClick={() => setOpenSidebar(!openSidebar)}
          >
            {openSidebar === false ? (
              <MenuIcon id="hamburger-closed" />
            ) : (
              <CloseIcon id="hamburger-opened" />
            )}
          </IconButton>
          <h4 id="portal-heading">{heading}</h4>
        </div>
        {heading.includes("Dashboard") ? (
          <Button
            id="create-new-request-btn"
            color="inherit"
            onClick={handleCreateNewRequest}
            disabled={openModal || openSidebar}
          >
            <AddIcon id="add-request" />
            <span className="appbar-btn-text" id="add-request-span">
              New Request
            </span>
          </Button>
        ) : (
          <Button
            id="return-home-btn"
            aria-label="return-to-dashboard"
            onClick={handleReturnToDashboard}
            disabled={openModal || openSidebar}
            onMouseOver={async (e) => {
              try {
                if (isAdmin) {
                  await queryClient.prefetchQuery({
                    queryKey: ["adminRows"],
                    queryFn: () => fetchAdminRows(isAdmin),
                  });
                } else {
                  await queryClient.prefetchQuery({
                    queryKey: ["clientRows", clientId, userId],
                    queryFn: () => fetchClientRows(clientId, userId),
                  });
                }
              } catch (error) {
                console.log("Prefetch error");
              }
            }}
          >
            <HomeIcon id="return-home-icon" />
            <span className="appbar-btn-text" id="return-home-span">
              Return Home
            </span>
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default DashboardAppBar;
